/**
 * Deriving the khutbah outline from its passages.
 *
 * Separate from `SectionsRail.tsx` because fast refresh only works when a file
 * exports components alone — and these are pure functions of the passages,
 * which is exactly why they are derived during render rather than stored.
 */
import type { PassageReading } from './types';

const SECTION_LABELS: Record<string, string> = {
  opening: 'Opening',
  first_khutbah: 'First khutbah',
  second_khutbah: 'Second khutbah',
  supplication: 'Supplication',
  footnotes: 'Footnotes',
  other: 'Other',
};

export interface RailSection {
  sectionType: string;
  label: string;
  /** Null for a Word original, which has no pages. */
  pageStart: number | null;
  pageEnd: number | null;
  /** The passage to scroll to. */
  anchorPassageId: string;
}

export function railSections(passages: readonly PassageReading[]): RailSection[] {
  const sections: RailSection[] = [];
  for (const passage of passages) {
    const last = sections.at(-1);
    if (last && last.sectionType === passage.sectionType) {
      if (passage.pageNo !== null) {
        last.pageEnd = passage.pageNo;
        last.pageStart ??= passage.pageNo;
      }
      continue;
    }
    sections.push({
      sectionType: passage.sectionType,
      label: SECTION_LABELS[passage.sectionType] ?? SECTION_LABELS.other!,
      pageStart: passage.pageNo,
      pageEnd: passage.pageNo,
      anchorPassageId: passage.passageId,
    });
  }
  return sections;
}

export interface QuotedGroup {
  kind: 'quran' | 'hadith';
  label: string;
  passages: readonly PassageReading[];
}

export function quotedGroups(passages: readonly PassageReading[]): QuotedGroup[] {
  const quran = passages.filter((p) => p.passageType === 'quran');
  const hadith = passages.filter((p) => p.passageType === 'hadith');
  const groups: QuotedGroup[] = [];
  if (quran.length > 0) {
    groups.push({ kind: 'quran', label: 'Qur’an', passages: quran });
  }
  if (hadith.length > 0) {
    groups.push({ kind: 'hadith', label: 'Hadith', passages: hadith });
  }
  return groups;
}

/** A page range, or nothing at all for a source that has no pages. */
export function pageRange(section: RailSection): string | null {
  if (section.pageStart === null) {
    return null;
  }
  if (section.pageEnd === null || section.pageEnd === section.pageStart) {
    return `p. ${section.pageStart}`;
  }
  return `pp. ${section.pageStart}–${section.pageEnd}`;
}
